<script setup lang="ts">
import { useSpaces } from "@/api/space-query";
import AppAvatar from "@/components/common/AppAvatar.vue";
import AppDataTable from "@/components/common/AppDataTable.vue";
import Frame from "@/components/common/Frame.vue";
import CreateSpaceDialog from "@/components/dialogs/CreateSpaceDialog.vue";
import { icWorkspacesOutline } from "@/components/icons";
import PermissionGuard from "@/components/permission/PermissionGuard.vue";
import Drawer from "@/layouts/drawer/Drawer.vue";
import DrawerLayout from "@/layouts/drawer/DrawerLayout.vue";
import { DrawerSelection } from "@/layouts/drawer/use-drawer-data";
import { useDrawerState, useDrawerStore } from "@/stores";
import { useScopeStore } from "@/stores/scope";
import { nextRender } from "@/utils/render";
import { mdiCogOutline, mdiEye } from "@mdi/js";
import { Button, Icon, LinkButton, makeColumnHelper, Tooltip } from "@soft-db/ui";
import { storeToRefs } from "pinia";

type SpaceRowData = {
	id: string;
	logo: string;
	name: string;
	sites: number;
	stations: number;
	users: number;
	pages: number;
	reports: number;
};

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const { spaces, isLoading } = useSpaces();

const { toggleDrawer } = useDrawerStore();
const drawerState = useDrawerState();
const { filter } = storeToRefs(drawerState);

function changePage(selection: DrawerSelection) {
	router.push({
		name: "spaces-id",
		params: { id: selection.id },
		query: { ...route.query },
	});
	toggleDrawer();
}

const createSpaceDialog = ref(false);
function openCreateSpaceDialog() {
	createSpaceDialog.value = true;
}

function goToSpace(id: string) {
	router.push({ name: "spaces-id", params: { id: id } });
}

const spaceRows = computed<SpaceRowData[]>(() => {
	if (!spaces.value) return [];

	return spaces.value.map((space) => ({
		id: space.id,
		logo: space.logoUrl,
		name: space.name,
		sites: space.sites,
		stations: space.stations,
		users: space.users,
		pages: space.publicPage,
		reports: space.reports,
	}));
});

const { Column } = makeColumnHelper<SpaceRowData>();

const scopeStore = useScopeStore();

async function viewSpace(id: string) {
	drawerState.clear();
	await nextRender();
	await scopeStore.setContext("space", id);
}

// drawerState.clear();
</script>

<template>
	<DrawerLayout mobileOnly>
		<template #drawer>
			<Drawer :objects="['space']" @click="changePage" />
		</template>
		<Frame class="px-0!" topMargin>
			<AppDataTable
				v-model:filter="filter"
				:data="spaceRows"
				:loading="isLoading"
				stateStorage="url"
			>
				<template #title>
					<h1 class="flex items-center gap-2 text-center capitalize">
						<Icon size="lg" :d="icWorkspacesOutline" />
						{{ t("object.space", 2) }}
					</h1>
				</template>
				<template #action>
					<PermissionGuard permission="space.create">
						<Button variant="outline" @click="openCreateSpaceDialog">
							{{ t("space.createSpace") }}
						</Button>
					</PermissionGuard>
				</template>
				<template #columns>
					<Column
						v-slot="{ row }"
						field="name"
						:header="t('common.name')"
						enableSorting
						enableGlobalFilter
						mobileMain
					>
						<div class="flex flex-row items-center gap-2">
							<AppAvatar class="size-8 overflow-clip rounded-lg" :src="row.original.logo" />
							<span>{{ row.original.name }}</span>
						</div>
					</Column>
					<Column
						field="sites"
						:header="t('object.project', 2)"
						enableSorting
						enableGlobalFilter
						sortingFn="alphanumeric"
					/>
					<Column
						field="stations"
						:header="t('object.station', 2)"
						enableSorting
						enableGlobalFilter
						sortingFn="alphanumeric"
					/>
					<Column
						field="users"
						:header="t('object.user', 2)"
						enableSorting
						enableGlobalFilter
						sortingFn="alphanumeric"
					/>
					<Column
						field="pages"
						:header="t('space.publicPages')"
						enableSorting
						enableGlobalFilter
						sortingFn="alphanumeric"
					/>
					<!-- TODO: Add or not? (not shown in sub page) -->
					<!-- <Column
						field="reports"
						:header="t('object.report', 2)"
						enableSorting
						enableGlobalFilter
						sortingFn="alphanumeric"
					/> -->
					<Column v-slot="{ row }" shrink>
						<div class="flex gap-1">
							<Tooltip :tooltip="t('space.view')">
								<Button
									variant="soft"
									size="sm"
									color="primary"
									@click="() => viewSpace(row.original.id)"
								>
									<Icon :d="mdiEye" />
								</Button>
							</Tooltip>
							<Tooltip :tooltip="t('common.settings')">
								<LinkButton
									variant="soft"
									color="neutral"
									size="sm"
									:to="{ name: 'spaces-id', params: { id: row.original.id } }"
								>
									<Icon :d="mdiCogOutline" />
								</LinkButton>
							</Tooltip>
						</div>
					</Column>
				</template>
			</AppDataTable>

			<div class="flex justify-between"></div>
		</Frame>
		<CreateSpaceDialog v-model:dialog="createSpaceDialog" @created="goToSpace" />
	</DrawerLayout>
</template>
